import { FC } from "react";
import { ConfirmationStep } from "../../components/confirmationStep/ConfirmationStep";
import InfoBox from "../../components/infoBox/InfoBox";
import { InsertCodeStep } from "../../components/insertCodeStep/InsertCodeStep";
import { SendEmailStep } from "../../components/sendEmailStep/SendEmailStep";
import { Layout } from "../../containers/layout";
import { useSubmitResetEmail } from "../../hooks/useSubmitResetEmail/useSubmitResetEmail";
import { SubmitEmailDto } from "../../models/signupEmailDto";
import {
  useResetPasswordFlow,
  ResetPasswordStepEnum,
  ResetPasswordFlowProvider
} from "../../contexts/resetPasswordContext";
import { PasswordResetDto } from "../../models/PasswordResetDto";
import { NewPasswordDto } from "../../models/NewPasswordDto";
import { useResetPassword } from "../../hooks/useResetPassword/useResetPassword";
import { ResetPasswordStep } from "../../components/resetPasswordStep/ResetPasswordStep";
import { useSubmitCode } from "../../hooks/useSubmitCode/useSubmitCode";
import { useTranslation } from "../../hooks/useTranslationHook/useTranslation";
import { useToastNotification } from "../../hooks/useToastNotification/useToastNotification";
import { useNavigate } from "react-router-dom";
import { useWorkflowIdWithNotification } from "../../hooks/useGetWorkflowId/useWorkflowIdWithNotification";
import { TranslationKeyType } from "../../hooks/useTranslationHook/types";
import { navigateSecurely } from "../../utils/navigation/navigationUtils";

export const ResetPasswordPage: FC = () => {
  const {
    currentStep,
    setCurrentStep,
    setEmail,
    email,
    workflowId,
    setWorkflowId,
    resetFlow,
    setLanguage,
    language,
    mode
  } = useResetPasswordFlow();
  const { getWorkflowIdSafely } = useWorkflowIdWithNotification();
  const { submitCode, loading: codeLoading } = useSubmitCode();
  const { translateString } = useTranslation();
  const { submitResetEmail, loading: emailLoading } = useSubmitResetEmail();
  const { resetPassword } = useResetPassword();
  const { showNotification, NotificationElement } = useToastNotification();
  const navigate = useNavigate();

  const handleCancel = async () => {
    resetFlow();

    const search = window.location.search;
    if (mode === "reset") {
      navigate(`/reset-password${search}`, { replace: true });
    } else {
      navigate(`/change-password${search}`, { replace: true });
    }
  };

  const handleEmailSubmit = async (passwordResetDto: PasswordResetDto) => {
    try {
      await submitResetEmail(passwordResetDto);
      setEmail(passwordResetDto.email);
      setWorkflowId(passwordResetDto.workflowId);
      setLanguage(passwordResetDto.language);
      setCurrentStep(ResetPasswordStepEnum.InsertCode);
    } catch (err) {
      showNotification(translateString("error_sending_email"), "error");
    }
  };

  const handleCodeSubmit = async (code: string) => {
    if (!workflowId) {
      showNotification(translateString("workflow_missing_error"), "error");
      return;
    }

    try {
      await submitCode(code, workflowId);
      setCurrentStep(ResetPasswordStepEnum.ResetPassword);
    } catch (err: any) {
      const responseBody = err.response?.data || err;

      const errors = Array.isArray(responseBody?.errors)
        ? (responseBody.errors as BackendErrorDetail[])
        : [];

      if (errors.length > 0) {
        errors.forEach((errorDetail) => {
          const key = errorDetail.code as TranslationKeyType;
          const msg = translateString(key) ?? errorDetail.message;
          showNotification(msg, "error");
        });
      } else {
        showNotification(translateString("error_verifying_code"), "error");
      }
    }
  };

  const handleResendEmail = async () => {
    if (!email) {
      showNotification(translateString("workflow_missing_error"), "error");
      return;
    }
    const newWorkflowId = await getWorkflowIdSafely();
    if (!newWorkflowId) return;

    setWorkflowId(newWorkflowId);

    const signupDto: SubmitEmailDto = {
      email,
      workflowId: newWorkflowId,
      language: language ?? ""
    };

    await submitResetEmail(signupDto);
    showNotification(translateString("verification_email_info"), "info");
  };

  const handlePasswordSubmit = async (newPasswordDto: NewPasswordDto) => {
    try {
      await resetPassword(newPasswordDto);
      setCurrentStep(ResetPasswordStepEnum.Confirmation);
    } catch (err: unknown) {
      const errors = Array.isArray(err) ? (err as BackendErrorDetail[]) : [];

      errors.forEach((err) => {
        const key = err.code as TranslationKeyType;
        const msg = translateString(key) ?? err.message;
        showNotification(msg, "error");
      });
    }
  };

  const handleReturnToLogin = () => {
    navigateSecurely();
  };

  const renderStep = () => {
    switch (currentStep) {
      case ResetPasswordStepEnum.SendEmail:
        return (
          <SendEmailStep
            title={
              mode === "change"
                ? translateString("change_your_password")
                : translateString("forgot_password")
            }
            description=""
            infoBox={
              <InfoBox type="primary">{translateString("forgot_password_information")}</InfoBox>
            }
            loading={emailLoading}
            showReturnToLogin={mode === "reset"}
            onSubmit={handleEmailSubmit}
          />
        );
      case ResetPasswordStepEnum.InsertCode:
        return (
          <InsertCodeStep
            onSubmit={handleCodeSubmit}
            onResend={handleResendEmail}
            loading={codeLoading}
            resendLoading={emailLoading}
            onCancel={handleCancel}
          />
        );
      case ResetPasswordStepEnum.ResetPassword:
        return (
          <ResetPasswordStep
            onSubmit={handlePasswordSubmit}
            email={email ?? ""}
            workflowId={workflowId ?? ""}
            onCancel={handleCancel}
          />
        );
      case ResetPasswordStepEnum.Confirmation:
        return (
          <ConfirmationStep
            cardTitle={translateString("password_updated")}
            message={translateString("password_updated_description")}
            buttonText={translateString("return_to_login")}
            onButtonClick={handleReturnToLogin}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Layout>
      {renderStep()}
      {NotificationElement}
    </Layout>
  );
};

interface ResetPasswordProviderProps {
  mode: "reset" | "change";
}

export const ResetPasswordPageWithProvider: FC<ResetPasswordProviderProps> = ({ mode }) => (
  <ResetPasswordFlowProvider mode={mode}>
    <ResetPasswordPage />
  </ResetPasswordFlowProvider>
);
